import { formatCalendarDateToShortUs } from '@core/utils/time.utils';

const MSG_NO_OPEN_SLOTS = 'No open slots are available for this truck type requirement.';
const MSG_COMPANY_NOT_APPROVED = 'Your company must be approved before assigning drivers.';
const MSG_COMPANY_SUSPENDED = 'Your company account is suspended.';

/**
 * User-facing messages for POST /jobs/:jobId/ttrs/:ttrId/claim (and related) API error `code` values.
 * Prefer these over raw backend strings for consistent UI copy (English only).
 */
export const CLAIM_API_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  // 422 — slot / job
  NO_OPEN_SLOTS: MSG_NO_OPEN_SLOTS,
  JOB_NOT_CLAIMABLE: 'This job is not accepting assignments.',
  SLOT_DATE_PAST: 'You cannot assign a driver to a past date.',

  // 422 — company
  COMPANY_NOT_FOUND: 'Company was not found.',
  COMPANY_NOT_APPROVED: MSG_COMPANY_NOT_APPROVED,
  COMPANY_SUSPENDED: MSG_COMPANY_SUSPENDED,
  COMPANY_DOCS_INVALID:
    'Your company documents are missing, expired, or rejected. Please update them and try again.',

  // 422 — driver
  DRIVER_NOT_FOUND: 'The selected driver was not found or is not available to your company.',
  USER_DEACTIVATED: 'This driver account is deactivated.',
  DRIVER_INACTIVE: 'This driver is not active.',
  DRIVER_NOT_APPROVED: 'This driver must be approved before they can be assigned.',
  DRIVER_DOCS_INVALID:
    'This driver’s documents are missing, expired, or rejected. Please update them and try again.',
  DRIVER_UNAVAILABLE: 'This driver is already assigned on one or more dates required for this job.',

  // 422 — truck
  TRUCK_NOT_FOUND: 'The selected truck was not found or is not available to your company.',
  TRUCK_NOT_ACTIVE: 'This truck is not active.',
  TRUCK_TYPE_MISMATCH: 'This truck type does not match the job requirement.',
  TRUCK_NOT_APPROVED: 'This truck must be approved before it can be used.',
  TRUCK_EQUIPMENT_MISMATCH: 'This truck is missing required equipment for this job.',
  TRUCK_DOCS_INVALID:
    'This truck’s documents are missing, expired, or rejected. Please update them and try again.',
  TRUCK_UNAVAILABLE: 'This truck is already assigned on one or more dates required for this job.',

  // 403
  COMPANY_REQUIRED: 'Your account must be linked to a company before you can assign drivers.',
};

/** Marketplace / job detail `claimBlockedReason` (preview), aligned with claim errors where applicable. */
export const MARKETPLACE_CLAIM_BLOCKED_MESSAGES: Readonly<Record<string, string>> = {
  ADMIN_CANNOT_CLAIM: 'Driver assignment is not available for this account.',
  NO_OPEN_SLOTS: MSG_NO_OPEN_SLOTS,
  COMPANY_NOT_APPROVED: MSG_COMPANY_NOT_APPROVED,
  COMPANY_SUSPENDED: MSG_COMPANY_SUSPENDED,
  COMPANY_DOCS_INVALID:
    'Your company documents are missing, expired, or rejected. Please update them and try again.',
  DRIVER_DOCS_INVALID:
    'Driver documents are missing, expired, or rejected. Please update them and try again.',
  TRUCK_DOCS_INVALID:
    'Truck documents are missing, expired, or rejected. Please update them and try again.',
  DRIVER_UNAVAILABLE: 'This driver is already assigned on one or more dates required for this job.',
  TRUCK_UNAVAILABLE: 'This truck is already assigned on one or more dates required for this job.',
};

const DEFAULT_MARKETPLACE_BLOCKED =
  'Driver assignment is not available for this job at the moment.';

/** Shown when POST claim returns 409 (slot taken / cascade conflict) without a structured `code`. */
export const CLAIM_SLOT_CONFLICT_USER_MESSAGE =
  'This slot is no longer available. Please refresh and try again.';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function formatUnavailableDates(details: unknown): string {
  if (!isRecord(details)) return '';
  const raw = details['unavailableDates'];
  if (!Array.isArray(raw) || raw.length === 0) return '';
  const labels = raw
    .filter((d): d is string => typeof d === 'string' && d.trim().length > 0)
    .map(d => formatCalendarDateToShortUs(d))
    .filter(s => s.length > 0);
  if (labels.length === 0) return '';
  return ` Conflicting dates: ${labels.join(', ')}.`;
}

function formatInvalidDocsHint(details: unknown): string {
  if (!isRecord(details)) return '';
  const raw = details['invalidDocs'];
  if (!Array.isArray(raw) || raw.length === 0) return '';
  const types = raw
    .map((item): string => {
      if (!isRecord(item)) return '';
      const t = item['documentType'];
      return typeof t === 'string' && t.trim() ? t.trim() : '';
    })
    .filter(Boolean);
  if (types.length === 0) return '';
  return ` Affected documents: ${types.join(', ')}.`;
}

function formatMissingEquipment(details: unknown): string {
  if (!isRecord(details)) return '';
  const raw = details['missingEquipment'];
  if (!Array.isArray(raw) || raw.length === 0) return '';
  const ids = raw.filter((id): id is string => typeof id === 'string' && id.trim().length > 0);
  if (ids.length === 0) return '';
  return ` Missing equipment IDs: ${ids.join(', ')}.`;
}

/**
 * Returns a user-facing message when the API error body includes a known `code` (422/403, etc.).
 * Appends short detail hints when `details` contains unavailable dates, invalid docs, or equipment.
 */
export function resolveClaimApiErrorMessage(errorBody: unknown): string | null {
  if (!isRecord(errorBody)) return null;
  const codeRaw = errorBody['code'];
  const code = typeof codeRaw === 'string' && codeRaw.trim() ? codeRaw.trim() : '';
  if (!code) return null;

  const mapped = CLAIM_API_ERROR_MESSAGES[code];
  const apiMessage = errorBody['message'];
  const fallback = typeof apiMessage === 'string' && apiMessage.trim() ? apiMessage.trim() : '';
  let base = mapped ?? fallback;
  if (!base) return null;

  const details = errorBody['details'];
  if (code === 'DRIVER_UNAVAILABLE' || code === 'TRUCK_UNAVAILABLE') {
    base += formatUnavailableDates(details);
  } else if (
    code === 'COMPANY_DOCS_INVALID' ||
    code === 'DRIVER_DOCS_INVALID' ||
    code === 'TRUCK_DOCS_INVALID'
  ) {
    base += formatInvalidDocsHint(details);
  } else if (code === 'TRUCK_EQUIPMENT_MISMATCH') {
    base += formatMissingEquipment(details);
  }

  return base;
}

/** True when 403 should show API message only (no forced redirect to login). */
export function isForbiddenBusinessErrorWithoutLoginRedirect(errorBody: unknown): boolean {
  if (!isRecord(errorBody)) return false;
  const code = errorBody['code'];
  return (
    code === 'COMPANY_REQUIRED' ||
    code === 'WORK_SESSION_NOT_FOUND' ||
    code === 'INVALID_FOREMAN_POOL_MEMBER' ||
    code === 'SELF_REVIEW_PROHIBITED'
  );
}

export function getMarketplaceClaimBlockedReasonMessage(reason: string | null | undefined): string {
  if (!reason || typeof reason !== 'string') {
    return DEFAULT_MARKETPLACE_BLOCKED;
  }
  return (
    MARKETPLACE_CLAIM_BLOCKED_MESSAGES[reason] ??
    CLAIM_API_ERROR_MESSAGES[reason] ??
    DEFAULT_MARKETPLACE_BLOCKED
  );
}
